import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { statsArticles } from "@/data/statsArticles";

const PATH = "/stats";

const StatsHub = () => {
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "US Dating Statistics",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: canonicalUrl(PATH),
    structuredData: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "US Dating Statistics Hub",
      description: meta?.description,
      url: canonicalUrl(PATH),
      hasPart: statsArticles.map((a) => ({
        "@type": "Article",
        headline: a.title,
        url: canonicalUrl(`/stats/${a.slug}`),
      })),
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-10">
          <header className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">US Dating Statistics</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Research-grade statistics on the US dating pool, sourced from the Census
              Bureau, CDC, Pew Research, and BLS. The same data that powers our{" "}
              <Link to="/" className="text-primary hover:underline">
                Female Partner Calculator
              </Link>
              ,{" "}
              <Link to="/ideal-woman-calculator" className="text-primary hover:underline">
                Ideal Woman Calculator
              </Link>
              , and{" "}
              <Link to="/male-partner-calculator" className="text-primary hover:underline">
                Male Partner Calculator
              </Link>
              .
            </p>
          </header>

          <main className="grid md:grid-cols-2 gap-6">
            {statsArticles.map((article) => (
              <Card key={article.slug} className="hover:shadow-lg transition-shadow flex flex-col">
                <CardHeader>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-3xl font-extrabold text-primary">
                      {article.heroStat.value}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {article.heroStat.label}
                    </span>
                  </div>
                  <CardTitle className="text-xl">
                    <Link
                      to={`/stats/${article.slug}`}
                      className="hover:text-primary transition-colors"
                    >
                      {article.title}
                    </Link>
                  </CardTitle>
                  <CardDescription>{article.metaDescription}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Link
                    to={`/stats/${article.slug}`}
                    className="text-primary hover:underline text-sm font-medium"
                  >
                    Read the full statistics →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </main>

          <section className="bg-muted/50 rounded-lg p-6 text-center space-y-3">
            <h2 className="text-2xl font-semibold">Turn statistics into your number</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every figure above is one filter in our probability model. Combine them to
              see exactly how many people match your standards.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-2">
              <Link to="/" className="text-primary hover:underline font-medium">
                Female Partner Calculator →
              </Link>
              <Link to="/ideal-woman-calculator" className="text-primary hover:underline font-medium">
                Ideal Woman Calculator →
              </Link>
              <Link to="/male-partner-calculator" className="text-primary hover:underline font-medium">
                Male Partner Calculator →
              </Link>
              <Link to="/dating-standards-calculator" className="text-primary hover:underline font-medium">
                Dating Standards Test →
              </Link>
              <Link to="/states" className="text-primary hover:underline font-medium">
                Statistics by State →
              </Link>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StatsHub;
